import { IHierarchyVm, INodeSelectionCriteria } from '@aon/tws-common-ui-hierarchy';
import { bubbleUpHierarchyNodeUpdates } from '@aon/tws-common-ui-hierarchy/build/lib/store/HierarchyGraphStore';
import { HashTable, INodeVm } from '@aon/tws-common-ui-hierarchy/build/lib/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { TargetValueType } from '../models';
import * as HierarchyAPI from '../services/HierarchyAPI';
import { IApplicationState } from './';
import { initialState as QuestionBranchingLogicInitialState, IQuestionBranchingLogicState } from './QuestionBranchingLogicSlice';

// Hierarchy graph store copied from common hierarchy
const fetchNode = createAsyncThunk(
	'branchingLogicHierarchy/fetchNode',
	async (props: { conditionIndex: number; branchingLogicIndex: number; nodeId: string; expandPath: boolean | undefined }, { getState }) => {
		const { branchingLogicIndex, conditionIndex, nodeId, expandPath } = props;
		const { questionBranchingLogic } = getState() as IApplicationState;
		const { hierarchy } = getQuestionBranchingLogicState(questionBranchingLogic, conditionIndex, branchingLogicIndex);

		if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
			return;
		}

		const hierarchyId = getHierarchyMetadataId(hierarchy);
		return HierarchyAPI.getHierarchy({ hierarchyId, nodeId, expandPath });
	},
);

const fetchNodAncestors = createAsyncThunk(
	'branchingLogicHierarchy/fetchNodAncestors',
	async (props: { conditionIndex: number; branchingLogicIndex: number; hierarchyNodeIds: string[] }, { getState }) => {
		const { branchingLogicIndex, conditionIndex, hierarchyNodeIds } = props;
		const { questionBranchingLogic } = getState() as IApplicationState;
		const { hierarchy } = getQuestionBranchingLogicState(questionBranchingLogic, conditionIndex, branchingLogicIndex);

		if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
			return;
		}

		const hierarchyId = getHierarchyMetadataId(hierarchy);

		return HierarchyAPI.fetchAncestorOf(hierarchyNodeIds, hierarchyId);
	},
);

const fetchHierarchy = createAsyncThunk(
	'branchingLogicHierarchy/fetchHierarchy',
	async (props: { conditionIndex: number; branchingLogicIndex: number; hierarchyMetadataId: string }) => {
		return getHierarchy(props.hierarchyMetadataId);
	},
);

const getHierarchy = async (hierarchyMetadataId: string) => {
	return HierarchyAPI.getHierarchy({
		hierarchyId: hierarchyMetadataId,
		includeOwners: false,
	});
};

const branchingLogicHierarchySlice = createSlice({
	name: 'branchingLogicHierarchy',
	initialState: QuestionBranchingLogicInitialState,
	reducers: {
		toggleNode: (state, action: PayloadAction<{ conditionIndex: number; branchingLogicIndex: number; nodeId: string }>) => {
			const {
				payload: { nodeId, conditionIndex, branchingLogicIndex },
			} = action;

			const { hierarchy } = getQuestionBranchingLogicState(state, conditionIndex, branchingLogicIndex);

			// if it's expanded then we just have to collapse it or If the node that was toggled was not found on the hierarchy state, do not apply any updates.
			if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData || !hierarchy.nodes?.[nodeId]) {
				state.validationModels = undefined;
				return;
			}

			const node = hierarchy.nodes[nodeId];

			const shouldNodeBeExpanded = !node.isExpanded;

			hierarchy.nodes = {
				...hierarchy.nodes,
				[nodeId]: {
					...node,
					isExpanded: shouldNodeBeExpanded,
				},
			};

			state.validationModels = undefined;
		},
		selectHierarchyCriteria: (
			state,
			action: PayloadAction<{ conditionIndex: number; branchingLogicIndex: number; selectionCriteria: INodeSelectionCriteria }>,
		) => {
			const {
				payload: { selectionCriteria, conditionIndex, branchingLogicIndex },
			} = action;

			let node = JSON.stringify(selectionCriteria);
			if (node === JSON.stringify({ descendantsIncluded: {}, singleIncluded: {} } as INodeSelectionCriteria)) {
				node = '';
			}

			const { condition } = getQuestionBranchingLogicState(state, conditionIndex, branchingLogicIndex);

			// This method is called on first load so we must compare changes or it will set unsavedBranchingLogicChanges=true even though we haven't made any changes.
			// JSON.stringify ends up in a different order than the string that comes from the server so the strings aren't equal, so we must do this to compare the objects.
			const prevSelectedCriteria: INodeSelectionCriteria =
				condition.displayLogicValues[0]?.valueText !== undefined
					? JSON.parse(condition.displayLogicValues[0].valueText)
					: ({ descendantsIncluded: {}, singleIncluded: {} } as INodeSelectionCriteria);

			if (_.isEqual(prevSelectedCriteria, selectionCriteria)) {
				return;
			}

			const values = condition.displayLogicValues;
			if (values.length === 0 || (values.length > 0 && node !== values[0].valueText)) {
				condition.displayLogicValues = [
					{
						targetValueType: TargetValueType.Response,
						valueText: node,
					},
				];
				state.unsavedBranchingLogicChanges = true;
				state.validationModels = undefined;
			}
		},
		hierarchyChange: (
			state,
			action: PayloadAction<{ conditionIndex: number; branchingLogicIndex: number; hierarchyMetadataId: string; hierarchyName: string }>,
		) => {
			const {
				payload: { conditionIndex, branchingLogicIndex, hierarchyMetadataId, hierarchyName },
			} = action;
			const { condition } = getQuestionBranchingLogicState(state, conditionIndex, branchingLogicIndex);
			condition.displayLogicValues = [];
			condition.conditionSourceId = hierarchyMetadataId;
			condition.conditionSourceText = hierarchyName;
			condition.hierarchy = undefined;
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
	},
	extraReducers: async (builder) => {
		builder
			.addCase(fetchNode.fulfilled, (state, action) => {
				const hierarchyVm = action.payload;
				if (hierarchyVm === undefined) return;

				const { nodeId, expandPath, conditionIndex, branchingLogicIndex } = action.meta.arg;
				const { hierarchy, condition } = getQuestionBranchingLogicState(state, conditionIndex, branchingLogicIndex);
				if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
					return;
				}

				// Update nodes hashtable based on the api response
				const existingNodes: HashTable<INodeVm> = { ...hierarchy.nodes };
				const existingParentIds: HashTable<string> = hierarchy.parentIds ? { ...hierarchy.parentIds } : {};

				if (hierarchyVm && hierarchyVm.nodes) {
					for (const item in hierarchyVm.nodes) {
						if (Object.prototype.hasOwnProperty.call(hierarchyVm.nodes, item)) {
							existingNodes[item] = { ...existingNodes[item], ...hierarchyVm.nodes[item] };
							existingNodes[item].childrenIds.forEach((childNodeId) => {
								existingParentIds[childNodeId] = item;
							});
						}
					}
				}

				// get new state
				const newHierarchyVm: IHierarchyVm = {
					...hierarchy,
					editInProgress: false,
					parentIds: existingParentIds,
					nodes: {
						...existingNodes,
						[nodeId]: {
							...existingNodes[nodeId],
							fullyLoaded: true,
						},
					},
				};

				bubbleUpHierarchyNodeUpdates(hierarchy, newHierarchyVm, nodeId, expandPath);

				if (expandPath) {
					newHierarchyVm.selectedNodeId = nodeId;
					newHierarchyVm.pendingSelectedNodeScroll = true;
				}
				condition.hierarchy = { ...newHierarchyVm, isLoading: false };
				state.isLoadingOrSaving = false;
			})
			.addCase(fetchNodAncestors.fulfilled, (state, action) => {
				const { conditionIndex, branchingLogicIndex, hierarchyNodeIds } = action.meta.arg;
				const { hierarchy, condition } = getQuestionBranchingLogicState(state, conditionIndex, branchingLogicIndex);

				if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData || !action.payload) {
					return;
				}

				const hierarchyVm = action.payload;

				const invalidNodeIds: HashTable<boolean> = {};
				const nodes: HashTable<INodeVm> = { ...hierarchy.nodes };
				const parentIds: HashTable<string> = hierarchy.parentIds ? { ...hierarchy.parentIds } : {};

				// Update nodes hashtable based on the api response
				for (const id in hierarchyNodeIds) {
					if (Object.prototype.hasOwnProperty.call(hierarchyNodeIds, id)) {
						const item = hierarchyNodeIds[id];
						const nodesList = hierarchyVm.nodes;

						if (nodesList === undefined) {
							continue;
						}

						if (!(hierarchyNodeIds[id] in nodesList)) {
							invalidNodeIds[item] = true;
						} else {
							nodes[item] = { ...nodesList[item], ...nodes[item] };

							if (nodes[item].childrenIds) {
								nodes[item].childrenIds.forEach((childNodeId) => {
									parentIds[childNodeId] = item;
								});
							}
						}
					}
				}

				condition.hierarchy = {
					...hierarchy,
					parentIds,
					nodes,
					isLoading: false,
					invalidNodeIds,
				};
			})
			.addCase(fetchHierarchy.pending, (state, action) => {
				const { conditionIndex, branchingLogicIndex } = action.meta.arg;
				const { condition } = getQuestionBranchingLogicState(state, conditionIndex, branchingLogicIndex);
				condition.hierarchy = { isLoading: true };
			})
			.addCase(fetchHierarchy.rejected, (state, action) => {
				const { conditionIndex, branchingLogicIndex } = action.meta.arg;
				const { condition } = getQuestionBranchingLogicState(state, conditionIndex, branchingLogicIndex);
				condition.hierarchy = { isLoading: false, couldNotFetchData: true };
			})
			.addCase(fetchHierarchy.fulfilled, (state, action) => {
				const { conditionIndex, branchingLogicIndex } = action.meta.arg;
				const { condition } = getQuestionBranchingLogicState(state, conditionIndex, branchingLogicIndex);
				condition.hierarchy = { ...action.payload, isLoading: false };
			});
	},
});

const getQuestionBranchingLogicState = (state: IQuestionBranchingLogicState, conditionIndex: number, branchingLogicIndex: number) => {
	const branchingLogicEditModel = state.branchingLogicEditModels[branchingLogicIndex];
	const condition = branchingLogicEditModel.displayLogicConditions[conditionIndex];
	const { hierarchy } = condition;
	return { condition, hierarchy };
};

const getHierarchyMetadataId = (hierarchyVm: IHierarchyVm) => {
	return hierarchyVm.hierarchyHeader?.hierarchyMetadataId ?? '';
};

export { fetchHierarchy, fetchNodAncestors, fetchNode };
export const { selectHierarchyCriteria, hierarchyChange, toggleNode } = branchingLogicHierarchySlice.actions;
export default branchingLogicHierarchySlice.reducer;
